import clsx from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { AlertErrorCircle, Document, DropdownTriangle, Files, Template, XClear } from '@/assets/icons'
import { selectAgentV2IsChatWritingSampleReplaced } from '@/agent-v2/chat-form/store/selectors'
import { selectAgentV2ConversationChatFileIds, selectAgentV2ConversationChatWritingSampleFileId } from '@/agent-v2/conversations/store/selectors'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import { useOpenChatAttachedFile } from '@/agent-v2/hooks/useOpenChatAttachedFile'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { getMenuItemClassName, getMenuItemContentClassNames } from '@/components/menu/menu-item-styles'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'
import TruncatedText from '@/components/truncated-text/TruncatedText'
import type { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { useUserFiles } from '@/user_files/files/hooks/useUserFiles'
import { useUserFilesPollingProcessor } from '@/user_files/polling/hooks/useUserFilesPollingProcessor'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { canOpenAttachedFile } from '@/agent-v2/utils/chat-attached-file'

const kUnloadedFileName = 'Could not load file'

type ChatFileRow = {
  fileId: string
  name: string
  isWritingSample: boolean
  isProcessing?: boolean
  /* Blocks the next turn until it's removed. The label says why, unless the row's name already does. */
  hasError?: boolean
  errorLabel?: string
  onOpen?: () => void
}

function ErrorIcon({ label }: { label?: string }) {
  return <AlertErrorCircle className="size-4 shrink-0 text-red-700" {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })} />
}

function ProcessingSpinner() {
  return (
    <span className="flex size-4 shrink-0 items-center justify-center">
      <CircularProgressContinuousSized size={12} thickness={7} />
    </span>
  )
}

/** The "Chat files (N)" button above the composer, listing the files every next turn sends with a way to remove each. */
export function ChatFilesDropdown({ conversationId, onLastFileRemoved }: { conversationId: string; onLastFileRemoved?: () => void }) {
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)
  const fileIds = useAppSelector((state: RootState) => selectAgentV2ConversationChatFileIds(state, { conversationId }))
  const chatWritingSampleFileId = useAppSelector((state: RootState) => selectAgentV2ConversationChatWritingSampleFileId(state, { conversationId }))
  // A writing sample picked in the composer replaces the chat's on the next turn, so the chat's isn't listed
  const isWritingSampleReplaced = useAppSelector((state: RootState) => selectAgentV2IsChatWritingSampleReplaced(state, { conversationId }))
  const writingSampleFileId = isWritingSampleReplaced ? null : chatWritingSampleFileId
  const { filesById, error: fetchError } = useUserFiles(writingSampleFileId ? [...fileIds, writingSampleFileId] : fileIds)
  const openChatAttachedFile = useOpenChatAttachedFile(conversationId)

  const contentRef = useRef<HTMLDivElement>(null)
  const removedRowIndexRef = useRef<number | null>(null)

  // A file shows by name once its record loads, or as a file that couldn't load when the fetch failed, so it can still be
  // removed. A deleted file leaves the chat's files as soon as it's known, so it never gets a row.
  const toRow = (fileId: string, isWritingSample: boolean): ChatFileRow | null => {
    const file = filesById[fileId]
    if (!file) return fetchError ? { fileId, name: kUnloadedFileName, isWritingSample, hasError: true } : null

    const onOpen = canOpenAttachedFile(file)
      ? () => {
          setOpen(false)
          openChatAttachedFile(file)
        }
      : undefined
    const isProcessing = file.processing_status === UserFileProcessingStatus.Pending || file.processing_status === UserFileProcessingStatus.Processing
    const hasError = file.processing_status === UserFileProcessingStatus.Failed
    return {
      fileId,
      name: file.original_filename,
      isWritingSample,
      isProcessing,
      hasError,
      errorLabel: hasError ? 'Processing failed' : undefined,
      onOpen,
    }
  }
  const rows = [writingSampleFileId ? toRow(writingSampleFileId, true) : null, ...fileIds.map((fileId) => toRow(fileId, false))].filter(
    (row): row is ChatFileRow => row !== null
  )

  // A processing file holds up the next turn, so its record is polled until it finishes and the spinner clears
  const processingFileIdsKey = rows
    .filter((row) => row.isProcessing)
    .map((row) => row.fileId)
    .join(',')
  const processingFileIds = useMemo(() => (processingFileIdsKey ? processingFileIdsKey.split(',') : []), [processingFileIdsKey])
  useUserFilesPollingProcessor({ sourceId: `chat-files-${conversationId}`, fileIds: processingFileIds, enabled: processingFileIds.length > 0 })

  const fileCount = fileIds.length + (writingSampleFileId ? 1 : 0)
  // A file with an error holds up the next turn until it's removed, so it shows over one that's still processing
  const hasErrorFile = rows.some((row) => row.hasError)

  // A removed row takes its focus with it, so focus moves to the row that took its place, or the one before it
  useLayoutEffect(() => {
    const removedRowIndex = removedRowIndexRef.current
    if (removedRowIndex === null) return
    removedRowIndexRef.current = null

    const removeButtons = contentRef.current?.querySelectorAll<HTMLElement>('[data-chat-file-remove]') ?? []
    const nextRemoveButton = removeButtons[Math.min(removedRowIndex, removeButtons.length - 1)]
    if (nextRemoveButton) nextRemoveButton.focus()
    else onLastFileRemoved?.()
  }, [fileIds, writingSampleFileId, onLastFileRemoved])

  // The list closes with its last file, so it doesn't reopen on its own when the chat next has files
  useEffect(() => {
    if (fileCount === 0) setOpen(false)
  }, [fileCount])

  if (fileCount === 0) return null

  return (
    <div className="flex justify-end pb-2.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1 rounded-md py-0.5 pl-2 pr-0.5 text-brand-neutral-950 hover:bg-brand-neutral-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 data-[state=open]:bg-brand-neutral-100"
          >
            {hasErrorFile ? (
              <ErrorIcon label="File error" />
            ) : processingFileIds.length > 0 ? (
              <ProcessingSpinner />
            ) : (
              <Files className="size-4 shrink-0" aria-hidden />
            )}
            <span className="py-0.5 text-sm font-medium leading-5">Chat files ({fileCount})</span>
            <DropdownTriangle className="size-[18px] shrink-0 text-brand-neutral-600" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent
          ref={contentRef}
          align="end"
          side="top"
          sideOffset={4}
          width="auto"
          showArrow={false}
          // Keeps a gap from the viewport edge, with the list scrolling once it's taller than the space left
          collisionPadding={16}
          // The menu surface, so the list reads as a dropdown rather than a popover card
          className="flex max-h-[var(--radix-popover-content-available-height)] w-[260px] flex-col gap-0.5 overflow-y-auto border border-brand-neutral-200 p-2 shadow-menu"
        >
          {rows.map((row, rowIndex) => {
            const contentClassNames = getMenuItemContentClassNames({ variant: 'neutral' })
            // A row that can't open keeps its resting colours on hover, like it keeps its background
            const iconClassName = twMerge(contentClassNames.iconClassName, !row.onOpen && 'group-hover:text-brand-neutral-600')
            const labelClassName = twMerge(contentClassNames.labelClassName, !row.onOpen && 'group-hover:text-brand-neutral-700')
            const Icon = row.isWritingSample ? Template : Document
            return (
              <div
                key={`${row.isWritingSample ? 'writing-sample' : 'file'}:${row.fileId}`}
                className={getMenuItemClassName({
                  variant: 'neutral',
                  // A row that can't open doesn't highlight on hover and takes the not-allowed cursor, like a disabled menu item
                  className: clsx('has-[:focus-visible]:bg-brand-neutral-100', !row.onOpen && 'hover:!bg-transparent !cursor-not-allowed'),
                })}
              >
                <button
                  type="button"
                  disabled={!row.onOpen}
                  onClick={row.onOpen}
                  className="flex min-w-0 flex-1 items-center gap-2.5 text-left focus:outline-hidden cursor-[inherit]"
                >
                  {row.hasError ? (
                    <ErrorIcon label={row.errorLabel} />
                  ) : row.isProcessing ? (
                    <ProcessingSpinner />
                  ) : (
                    <Icon className={iconClassName} aria-hidden />
                  )}
                  <TruncatedText className={labelClassName} disableOnTouch>
                    {row.name}
                  </TruncatedText>
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${row.name} from chat`}
                  data-chat-file-remove
                  onClick={() => {
                    removedRowIndexRef.current = rowIndex
                    dispatch(
                      row.isWritingSample
                        ? AgentV2ConversationsActions.clearChatWritingSample({ conversationId })
                        : AgentV2ConversationsActions.removeChatFile({ conversationId, fileId: row.fileId })
                    )
                  }}
                  className="shrink-0 rounded text-brand-neutral-600 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <XClear className="size-4" />
                </button>
              </div>
            )
          })}
        </PopoverContent>
      </Popover>
    </div>
  )
}
